import { User } from '../models/User';
import { UserRole } from '../value-objects/UserRole';
import { UserId } from '../value-objects/UserId';

/**
 * 권한 검증 결과
 */
export interface AuthorizationResult {
  allowed: boolean;
  reason?: string;
}

/**
 * 리소스 접근 권한 정의
 */
export interface Permission {
  resource: string;
  action: string;
  context?: Record<string, any>;
}

/**
 * 권한 검증 서비스
 * 사용자 역할 기반 접근 제어(RBAC) 구현
 */
export class AuthorizationService {
  /**
   * 사용자의 특정 권한 확인
   */
  static hasPermission(user: User, permission: Permission): AuthorizationResult {
    if (!user.isActive) {
      return {
        allowed: false,
        reason: '비활성화된 계정입니다.',
      };
    }

    if (user.isLocked()) {
      return {
        allowed: false,
        reason: '잠겨있는 계정입니다.',
      };
    }

    return this.checkRoleBasedPermission(user.role, permission);
  }

  /**
   * 역할 기반 권한 검증
   */
  private static checkRoleBasedPermission(
    role: UserRole,
    permission: Permission
  ): AuthorizationResult {
    const { resource, action } = permission;

    switch (role.value) {
      case 'admin':
        return this.checkAdminPermissions(resource, action);
      
      case 'owner':
        return this.checkOwnerPermissions(resource, action, permission.context);
      
      case 'student':
        return this.checkStudentPermissions(resource, action, permission.context);
      
      case 'parent':
        return this.checkParentPermissions(resource, action, permission.context);
      
      case 'guest':
        return this.checkGuestPermissions(resource, action);
      
      default:
        return {
          allowed: false,
          reason: '알 수 없는 사용자 역할입니다.',
        };
    }
  }

  /**
   * 관리자 권한 검증
   */
  private static checkAdminPermissions(resource: string, action: string): AuthorizationResult {
    // 관리자는 모든 리소스에 대한 모든 작업 허용
    return { allowed: true };
  }

  /**
   * 오너 권한 검증
   */
  private static checkOwnerPermissions(
    resource: string,
    action: string,
    context?: Record<string, any>
  ): AuthorizationResult {
    const ownerPermissions: { [key: string]: string[] } = {
      // 사용자 관리
      'users': ['read', 'create', 'update'],
      'students': ['read', 'create', 'update', 'delete'],
      'parents': ['read', 'create', 'update'],
      
      // 좌석 관리
      'seats': ['read', 'create', 'update', 'delete'],
      'seat-assignments': ['read', 'create', 'update', 'delete'],
      'seat-checkins': ['read', 'update'],
      
      // 결제 관리
      'payments': ['read', 'create'],
      'refunds': ['read', 'create'],
      'billing': ['read'],
      
      // 통계 및 리포트
      'statistics': ['read'],
      'reports': ['read'],
      
      // 시설 관리
      'facilities': ['read', 'update'],
      'zones': ['read', 'create', 'update', 'delete'],
      
      // 설정 관리
      'settings': ['read', 'update'],
      'organization': ['read', 'update'],
    };

    const allowedActions = ownerPermissions[resource];
    
    if (!allowedActions) {
      return {
        allowed: false,
        reason: `오너는 '${resource}' 리소스에 접근할 수 없습니다.`,
      };
    }

    if (!allowedActions.includes(action)) {
      return {
        allowed: false,
        reason: `오너는 '${resource}' 리소스에 대한 '${action}' 작업을 수행할 수 없습니다.`,
      };
    }

    return { allowed: true };
  }

  /**
   * 학생 권한 검증
   */
  private static checkStudentPermissions(
    resource: string,
    action: string,
    context?: Record<string, any>
  ): AuthorizationResult {
    const studentPermissions: { [key: string]: string[] } = {
      // 본인 정보 관리
      'profile': ['read', 'update'],
      'password': ['update'],
      
      // 좌석 관련
      'seats': ['read'],
      'seat-checkins': ['create', 'update'], // 본인 체크인/아웃만
      'my-seat-history': ['read'],
      
      // 결제 관련
      'my-payments': ['read'],
      'payment-methods': ['read', 'create', 'update'],
      
      // 통계 조회 (본인 것만)
      'my-statistics': ['read'],
      'study-time': ['read'],
      
      // 알림
      'notifications': ['read', 'update'],
    };

    const allowedActions = studentPermissions[resource];
    
    if (!allowedActions) {
      return {
        allowed: false,
        reason: `학생은 '${resource}' 리소스에 접근할 수 없습니다.`,
      };
    }

    if (!allowedActions.includes(action)) {
      return {
        allowed: false,
        reason: `학생은 '${resource}' 리소스에 대한 '${action}' 작업을 수행할 수 없습니다.`,
      };
    }

    // 컨텍스트 기반 추가 검증 (본인 데이터만 접근 가능)
    if (context?.requiresOwnership) {
      const currentUserId = context.currentUserId;
      const targetUserId = context.targetUserId;
      
      if (currentUserId !== targetUserId) {
        return {
          allowed: false,
          reason: '자신의 데이터만 접근할 수 있습니다.',
        };
      }
    }

    return { allowed: true };
  }

  /**
   * 학부모 권한 검증
   */
  private static checkParentPermissions(
    resource: string,
    action: string,
    context?: Record<string, any>
  ): AuthorizationResult {
    const parentPermissions: { [key: string]: string[] } = {
      // 본인 정보 관리
      'profile': ['read', 'update'],
      'password': ['update'],
      
      // 자녀 정보 조회
      'children': ['read'],
      'children-statistics': ['read'],
      'children-payments': ['read'],
      'children-seat-history': ['read'],
      
      // 결제 관련
      'my-payments': ['read'],
      'payment-methods': ['read', 'create', 'update'],
      
      // 알림
      'notifications': ['read', 'update'],
    };

    const allowedActions = parentPermissions[resource];
    
    if (!allowedActions) {
      return {
        allowed: false,
        reason: `학부모는 '${resource}' 리소스에 접근할 수 없습니다.`,
      };
    }

    if (!allowedActions.includes(action)) {
      return {
        allowed: false,
        reason: `학부모는 '${resource}' 리소스에 대한 '${action}' 작업을 수행할 수 없습니다.`,
      };
    }

    // 자녀 관계 검증
    if (context?.requiresChildRelation) {
      const parentId = context.parentId;
      const childId = context.childId;
      
      // 실제로는 데이터베이스에서 부모-자녀 관계 확인 필요
      if (!this.isChildOfParent(childId, parentId)) {
        return {
          allowed: false,
          reason: '자녀의 정보만 조회할 수 있습니다.',
        };
      }
    }

    return { allowed: true };
  }

  /**
   * 게스트 권한 검증
   */
  private static checkGuestPermissions(resource: string, action: string): AuthorizationResult {
    const guestPermissions: { [key: string]: string[] } = {
      // 공개 정보만 조회 가능
      'public-info': ['read'],
      'facilities': ['read'],
      'pricing': ['read'],
    };

    const allowedActions = guestPermissions[resource];
    
    if (!allowedActions) {
      return {
        allowed: false,
        reason: `게스트는 '${resource}' 리소스에 접근할 수 없습니다.`,
      };
    }

    if (!allowedActions.includes(action)) {
      return {
        allowed: false,
        reason: `게스트는 '${resource}' 리소스에 대한 '${action}' 작업을 수행할 수 없습니다.`,
      };
    }

    return { allowed: true };
  }

  /**
   * 다중 권한 검증
   */
  static hasAnyPermission(user: User, permissions: Permission[]): AuthorizationResult {
    for (const permission of permissions) {
      const result = this.hasPermission(user, permission);
      if (result.allowed) {
        return result;
      }
    }

    return {
      allowed: false,
      reason: '요청한 작업을 수행할 권한이 없습니다.',
    };
  }

  /**
   * 모든 권한 검증
   */
  static hasAllPermissions(user: User, permissions: Permission[]): AuthorizationResult {
    for (const permission of permissions) {
      const result = this.hasPermission(user, permission);
      if (!result.allowed) {
        return result;
      }
    }

    return { allowed: true };
  }

  /**
   * 역할 계층 확인
   */
  static hasRole(user: User, requiredRole: string): boolean {
    const roleHierarchy: { [key: string]: number } = {
      'admin': 5,
      'owner': 4,
      'student': 3,
      'parent': 2,
      'guest': 1,
    };

    const userRoleLevel = roleHierarchy[user.role.value] || 0;
    const requiredRoleLevel = roleHierarchy[requiredRole] || 0;

    return userRoleLevel >= requiredRoleLevel;
  }

  /**
   * 조직 내 권한 확인 (향후 확장용)
   */
  static hasOrganizationPermission(
    user: User,
    organizationId: string,
    permission: Permission
  ): AuthorizationResult {
    // 향후 멀티 테넌트 지원 시 구현
    return this.hasPermission(user, permission);
  }

  /**
   * 시간 기반 권한 확인 (운영 시간 등)
   */
  static hasTimeBasedPermission(
    user: User,
    permission: Permission,
    timeConstraints?: {
      allowedHours?: { start: number; end: number };
      allowedDays?: number[]; // 0-6 (일-토)
    }
  ): AuthorizationResult {
    const baseResult = this.hasPermission(user, permission);
    if (!baseResult.allowed) {
      return baseResult;
    }

    if (!timeConstraints) {
      return baseResult;
    }

    const now = new Date();
    const currentHour = now.getHours();
    const currentDay = now.getDay();

    // 시간 제한 확인
    if (timeConstraints.allowedHours) {
      const { start, end } = timeConstraints.allowedHours;
      if (currentHour < start || currentHour >= end) {
        return {
          allowed: false,
          reason: `운영 시간(${start}:00-${end}:00) 외에는 접근할 수 없습니다.`,
        };
      }
    }

    // 요일 제한 확인
    if (timeConstraints.allowedDays) {
      if (!timeConstraints.allowedDays.includes(currentDay)) {
        return {
          allowed: false,
          reason: '운영 요일이 아닙니다.',
        };
      }
    }

    return baseResult;
  }

  /**
   * 부모-자녀 관계 확인 (헬퍼 메서드)
   */
  private static isChildOfParent(childId: string, parentId: string): boolean {
    // 실제로는 데이터베이스 조회 필요
    // 임시로 true 반환
    return true;
  }

  /**
   * 권한 검증 데코레이터용 헬퍼
   */
  static createPermissionChecker(user: User) {
    return {
      can: (resource: string, action: string, context?: Record<string, any>) => {
        return this.hasPermission(user, { resource, action, context });
      },
      canAny: (permissions: Array<{ resource: string; action: string; context?: Record<string, any> }>) => {
        return this.hasAnyPermission(user, permissions);
      },
      canAll: (permissions: Array<{ resource: string; action: string; context?: Record<string, any> }>) => {
        return this.hasAllPermissions(user, permissions);
      },
      hasRole: (role: string) => {
        return this.hasRole(user, role);
      },
    };
  }
}